import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputBase,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { generatePath, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getProduct } from "../../utility/httpActions/productActions";
import ImageWithLoader from "../ImageWithLoader";
import { resolveImageUri } from "../../utility/constants";
import { frontendRoutes } from "../../utility/frontendRoutes";
import {
  buildProductPath,
  formatProductPrice,
  getEffectiveUnitPrice,
  getProductAvailableStock,
  isPriceHidden,
  isProductNegotiable,
  isProductOutOfStock,
} from "../../utility/utilityFunctions";
import VerifiedBadge from "../VerifiedBadge";
import AddToCartModal from "./AddToCartModal";
import ConvertedPrice from "./ConvertedPrice";

const CartItemCard = ({ cartItem, onUpdate, onQuantityClamped }) => {
  const productId = cartItem.product?.id || cartItem.product;
  const [isPending, setIsPending] = useState(false);

  const { data: product, isLoading } = useQuery({
    queryKey: ["product", productId],
    queryFn: () => getProduct(productId),
    enabled: !!productId,
  });

  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [offerError, setOfferError] = useState(null);
  const [isSavingOffer, setIsSavingOffer] = useState(false);

  // Saves the line's offer and note together (null clears either).
  const handleSaveOffer = ({ note, offerPriceCents }) => {
    setIsSavingOffer(true);
    setOfferError(null);
    onUpdate(
      {
        ...cartItem,
        product: { ...cartItem.product, id: productId },
        quantity: cartItem.quantity || 1,
        note,
        offerPriceCents,
      },
      {
        onSuccess: () => setOfferModalOpen(false),
        onError: (err) =>
          setOfferError(
            err?.data?.message || "Couldn't save your offer. Try again.",
          ),
        onSettled: () => setIsSavingOffer(false),
      },
    );
  };

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity < 0) return;
    setIsPending(true);
    // Quantity changes leave the line's offer and note as they are
    // (undefined = unchanged): resending a stale offer would refuse the update.
    onUpdate(
      {
        ...cartItem,
        product: { ...cartItem.product, id: productId },
        quantity: newQuantity,
        offerPriceCents: undefined,
        note: undefined,
      },
      { onSettled: () => setIsPending(false) },
    );
  };

  const currentQty = cartItem.quantity || 1;
  const isOutOfStock = isProductOutOfStock(product);
  const availableStock = getProductAvailableStock(product);
  const exceedsAvailableStock =
    !isOutOfStock && availableStock !== null && currentQty > availableStock;

  // Local editable copy of the quantity text, kept separate from currentQty
  // so the user can freely type/clear digits without every keystroke
  // fighting the server-derived value. Only re-synced from currentQty while
  // not focused, so an in-flight update or cart refetch can't clobber
  // what's being typed.
  const [quantityInput, setQuantityInput] = useState(String(currentQty));
  const [isQuantityFocused, setIsQuantityFocused] = useState(false);

  useEffect(() => {
    if (!isQuantityFocused) setQuantityInput(String(currentQty));
  }, [currentQty, isQuantityFocused]);

  const commitQuantityInput = () => {
    setIsQuantityFocused(false);
    const parsed = parseInt(quantityInput, 10);
    if (isNaN(parsed) || parsed < 1) {
      setQuantityInput(String(currentQty));
      return;
    }
    let finalQuantity = parsed;
    if (!isOutOfStock && availableStock !== null && parsed > availableStock) {
      finalQuantity = availableStock;
      onQuantityClamped?.(
        "Quantity adjusted. That's more than what's available.",
      );
    }
    setQuantityInput(String(finalQuantity));
    if (finalQuantity !== currentQty) {
      handleQuantityChange(finalQuantity);
    }
  };

  const price = product?.price || cartItem.product?.price || 0;
  // Offers are made in the app; the web cart shows them read-only.
  const hasOffer = cartItem.offerPriceCents != null;
  const offerInvalid = cartItem.offerStatus === "INVALID";
  // A hidden price is never sent to buyers: the line is priced by the
  // buyer's offer alone.
  const priceHidden = isPriceHidden(product || cartItem.product);
  const unitPrice = getEffectiveUnitPrice(cartItem, { price });
  const currency = product?.currency || cartItem.product?.currency;
  const imageUri = product?.images?.[0] ? resolveImageUri(product.images[0]) : null;
  const shopName = product?.shop?.shopName;
  // Falls back to the raw cart-item ID while `product` is still loading —
  // buildProductPath needs the fetched product for its slug.
  const productPath = product
    ? buildProductPath(product)
    : generatePath(frontendRoutes.VIEW_PRODUCT, { productId });

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        py: 2.5,
        borderBottom: "1px solid #f3f4f6",
        "&:last-child": { borderBottom: "none" },
        opacity: isPending ? 0.6 : 1,
        transition: "opacity 0.15s",
      }}
    >
      {/* Image */}
      <Box
        component={Link}
        to={productPath}
        sx={{
          width: 80,
          height: 80,
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: "#f5f5f5",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isLoading ? (
          <CircularProgress size={20} sx={{ color: "#046287" }} />
        ) : imageUri ? (
          <ImageWithLoader src={imageUri} alt={product?.productName} />
        ) : (
          <Typography color="text.secondary" fontSize={11}>No image</Typography>
        )}
      </Box>

      {/* Details */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            component={Link}
            to={productPath}
            sx={{
              fontWeight: 600,
              color: "inherit",
              textDecoration: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              "&:hover": { color: "#046287" },
              mb: 0.25,
            }}
          >
            {product?.productName || "Loading…"}
          </Typography>
          {isOutOfStock && (
            <Box
              sx={{
                px: 1,
                py: 0.1,
                bgcolor: "#ffedd5",
                borderRadius: 4,
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{ color: "#9a3412", fontSize: 11, fontWeight: 600 }}
              >
                Out of stock
              </Typography>
            </Box>
          )}
        </Box>
        {shopName && (
          <Typography
            variant="body2"
            color="text.secondary"
            fontSize={12}
            sx={{ mb: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}
          >
            {shopName}
            {product?.shop?.isIdentityVerified && <VerifiedBadge size={12} />}
          </Typography>
        )}
        <Typography
          variant="body2"
          color={hasOffer ? "text.secondary" : "#046287"}
          fontWeight={hasOffer ? 400 : 700}
          sx={{
            mb: hasOffer || cartItem.offerNotice ? 0.5 : 1.5,
            textDecoration: hasOffer && !priceHidden ? "line-through" : "none",
          }}
        >
          {priceHidden ? "Open to offers" : formatProductPrice(price, currency)}
        </Typography>
        <ConvertedPrice
          amount={unitPrice}
          currency={currency}
          sx={{ mt: -0.5, mb: 1 }}
        />
        {priceHidden && !hasOffer && (
          <Typography
            variant="caption"
            color={offerInvalid ? "error.main" : "text.secondary"}
            display="block"
            sx={{ mb: 1.5 }}
          >
            {cartItem.offerError ||
              "The seller doesn't show a price. Make an offer to buy this."}
          </Typography>
        )}
        {hasOffer && (
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="body2"
              fontSize={13}
              fontWeight={600}
              color={offerInvalid ? "error.main" : "success.dark"}
            >
              Your offer: {formatProductPrice(unitPrice, currency)} each
            </Typography>
            <Typography variant="caption" color={offerInvalid ? "error.main" : "text.secondary"}>
              {offerInvalid
                ? `${cartItem.offerError || "This offer can't be placed anymore."} Edit or remove it to check out.`
                : "The seller can accept, counter, or decline. Don't pay until you both agree."}
            </Typography>
          </Box>
        )}
        {cartItem.offerNotice === "PRICE_DROPPED_BELOW_OFFER" && (
          <Typography variant="caption" color="warning.dark" display="block" sx={{ mb: 1.5 }}>
            The price is now at or below your offer, so your offer was removed.
            You'll pay the listed price.
          </Typography>
        )}
        {cartItem.note && (
          <Typography
            variant="body2"
            color="text.secondary"
            fontSize={12}
            sx={{ mb: 1.5 }}
          >
            Note: {cartItem.note}
          </Typography>
        )}

        {/* Quantity controls + remove */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              border: "1px solid #e5e7eb",
              borderRadius: 2,
            }}
          >
            <IconButton
              size="small"
              onClick={() => handleQuantityChange(currentQty - 1)}
              disabled={isPending || isOutOfStock || currentQty <= 1}
              sx={{ p: 0.5 }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <InputBase
              value={quantityInput}
              onChange={(e) =>
                setQuantityInput(e.target.value.replace(/[^0-9]/g, ""))
              }
              onFocus={() => setIsQuantityFocused(true)}
              onBlur={commitQuantityInput}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.target.blur();
              }}
              disabled={isPending || isOutOfStock}
              inputProps={{
                inputMode: "numeric",
                maxLength: 4,
                style: {
                  minWidth: 24,
                  textAlign: "center",
                  fontWeight: 600,
                  fontSize: 14,
                  padding: 0,
                },
              }}
              sx={{ width: 32 }}
            />
            <IconButton
              size="small"
              onClick={() => handleQuantityChange(currentQty + 1)}
              disabled={
                isPending ||
                isOutOfStock ||
                (availableStock !== null && currentQty >= availableStock)
              }
              sx={{ p: 0.5 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {(isProductNegotiable(product) || hasOffer) && (
              <Button
                size="small"
                onClick={() => {
                  setOfferError(null);
                  setOfferModalOpen(true);
                }}
                disabled={isPending || isSavingOffer}
                sx={{ textTransform: "none", color: "#046287" }}
              >
                {hasOffer ? "Edit offer" : "Make an offer"}
              </Button>
            )}
            <Typography fontWeight={600} color="text.secondary" fontSize={14}>
              {priceHidden && !hasOffer
                ? "—"
                : formatProductPrice(unitPrice * currentQty, currency)}
            </Typography>
            <IconButton
              size="small"
              onClick={() => handleQuantityChange(0)}
              disabled={isPending}
              sx={{ color: "#ef4444", "&:hover": { bgcolor: "#fef2f2" } }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        {isOutOfStock ? (
          <Typography sx={{ color: "#c2410c", fontSize: 12, mt: 1 }}>
            This item is out of stock and won't be included at checkout.
          </Typography>
        ) : exceedsAvailableStock ? (
          <Typography sx={{ color: "#b45309", fontSize: 12, mt: 1 }}>
            Only {availableStock} left in stock. Please reduce the quantity.
          </Typography>
        ) : null}
      </Box>
      <AddToCartModal
        mode="edit"
        open={offerModalOpen}
        onClose={() => setOfferModalOpen(false)}
        onSubmit={handleSaveOffer}
        isSubmitting={isSavingOffer}
        product={product}
        quantity={currentQty}
        initialNote={cartItem.note}
        initialOfferPriceCents={cartItem.offerPriceCents ?? null}
        startWithOffer
        submitError={offerError}
      />
    </Box>
  );
};

export default CartItemCard;
